import { Extend } from './internal/typeHelpers';

/**
 * This domain should correspond with the ones defined in Jira issues.
 * If the one you're looking for is missing, please add it here and in backoffice.
 */
type Domain =
  | 'access'
  | 'ai'
  | 'appsec'
  | 'audit-log'
  | 'auth'
  | 'bpm'
  | 'broadcasts'
  | 'calculated-fields'
  | 'component-overview'
  | 'core'
  | 'core-viewpoints'
  | 'dashboards'
  | 'design-system'
  | 'discover'
  | 'discover-in-core'
  | 'fields'
  | 'gremlin-query'
  | 'grid-editor'
  | 'growth'
  | 'i18n'
  | 'integrations'
  | 'inventory'
  | 'metamodel-module'
  | 'metamodel-visualizations'
  | 'navigator'
  | 'perspectives'
  | 'presentations'
  | 'reports'
  | 'scenarios'
  | 'search'
  | 'solutions'
  | 'surveys'
  | 'tasks'
  | 'transformations'
  | 'visibility'
  | 'visualizations';

// More scopes can be added if there's a need for them
type Scope =
  | 'tmp' // work in progress, beta features
  | 'internal' // not for customers
  | 'paid' // only for paid plans
  | 'phased' // phasing out, but a few customers are lagging
  | 'restricted'; // only allow for certain customers

type FeatureNameSegment = string;

/**
 * <domain>_<scope>_<feature-name>
 * See https://ardoqcom.atlassian.net/wiki/spaces/TECH/pages/3734667277/Decision+Standardized+naming+of+feature+flags
 */
export type FeatureFlagType = `${Domain}_${Scope}_${FeatureNameSegment}`;

export type Feature = {
  persistedId: string;
  label: string;
  requiresOrgAdmin?: boolean;
  isEnabledForTrial?: boolean;
  requiresArdoqEmployee?: boolean;
};

type StrictFeature = Extend<Feature, { persistedId: FeatureFlagType }>;

const features = {
  COPY_WORKSPACES_BETWEEN_ORGS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'copy-workspaces-between-organizations',
    label: 'Copy workspaces between organizations',
  },
  PRESENTATIONS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'presentation',
    label: 'Presentations',
    isEnabledForTrial: true,
  },
  PUBLIC_PRESENTATIONS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'public-presentation',
    label: 'Public presentations',
    requiresOrgAdmin: true,
  },
  SURVEYS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'surveys',
    label: 'Surveys',
    isEnabledForTrial: true,
  },
  WEBHOOKS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'webhooks',
    label: 'Web hooks',
  },
  /** Deprecated view. */
  SHOW_OLD_RELATIONSHIP_VIEW_2: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'show-old-relationship-view-2',
    label: 'Old Relationship View / Network',
  },
  ROADMAP_FOR_HMCTS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'roadmap-for-hmcts',
    label: 'Roadmap',
  },
  BUNDLE_MANAGEMENT: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'bundle-management',
    label: 'Bundle management',
    requiresOrgAdmin: true,
  },
  BUNDLE_COPY_ASYNC: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'bundle-copy-async',
    label: 'Bundle copy async',
  },
  EXCEL_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'excel-enable',
    label: 'Enable Excel importer',
  },
  SERVICENOW_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'servicenow-enable',
    label: 'Enable ServiceNow integration',
  },
  AD_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ad-enable',
    label: 'Enable ActiveDirectory integration',
  },
  ENTRA_ID_UNIFIED_INTEGRATION_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'entra-id-unified-integration-enable',
    label: 'Enable Microsoft Entra ID unified integration',
  },
  AWS_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'aws-enable',
    label: 'Enable AWS integration',
  },
  AZURE_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-enable',
    label: 'Enable Azure integration',
  },
  AZURE_RESOURCES_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-resources-enable',
    label: 'Enable Azure Resources integration',
  },
  CELONIS_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'celonis-enable',
    label: 'Enable Celonis integration',
  },
  ARDOQ_IMPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ardoq-import-enable',
    label: 'Enable Ardoq integration',
  },
  ARDOQ_REFERENCE_DATA_IMPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ardoq-reference-data-import-enable',
    label: 'Enable Ardoq reference data integration',
  },
  ARDOQ_SAMPLE_DATA_IMPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ardoq-sample-data-import-enable',
    label: 'Enable Ardoq sample data integration',
  },
  WEBHOOKS_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'webhooks-enable',
    label: 'Enable WebHooks',
  },
  RESTAPI_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'restapi-enable',
    label: 'Enable REST API',
  },
  POWERBI_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'powerbi-enable',
    label: 'Enable PowerBI integration',
  },
  CONFLUENCE_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'confluence-enable',
    label: 'Enable Confluence integration',
  },
  SHAREPOINT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'sharepoint-enable',
    label: 'Enable Sharepoint integration',
  },
  SERVICENOW_EXPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'enable-servicenow-export',
    label: 'Enable Servicenow export integration',
  },
  SERVICENOW_GUIDED_EXPORT_ENABLE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'servicenow-guided-export-enable',
    label: 'Enable Servicenow export integration',
  },
  DISABLE_REST_API_V1_DOCS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-rest-api-v1-docs',
    label: 'Disable REST API v1 docs',
  },
  SCENARIOS_BETA: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'scenarios-beta',
    label: 'Scenarios Beta',
  },
  SEQUENTIAL_TRAVERSAL: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'sequentialTraversal',
    label: 'Sequential Traversal',
  },
  /** Viewpoint builder. Adds a "start set expansion" control that lets a traversal start from the selected components *and* their whole component-hierarchy subtree, without expressing it as a recursive parent-child path. */
  START_SET_EXPANSION_WITH_DESCENDANTS: {
    persistedId: 'core-viewpoints_tmp_start-set-expansion-with-descendants',
    label: 'Viewpoint builder: expand start set with descendants',
  },
  /** Experimental view developed for hype generation. Essentially block diagram with isometric rendering to look 3D. */
  ISOMETRIC_BLOCK_DIAGRAM: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'isometricBlockDiagram',
    label: 'Isometric Block Diagram (BETA)',
  },
  BROADCASTS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'broadcasts',
    label: 'Broadcasts',
    requiresOrgAdmin: true,
  },
  SALES_DEMO_TOUR_LAUNCHER: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'sales-demo-tour-launcher',
    label: 'Sales Demo Tour Launcher (ALPHA)',
    requiresArdoqEmployee: true,
  },
  SCIM_PROVISIONING: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'scim-provisioning',
    label: 'SCIM provisioning',
    requiresOrgAdmin: true,
  },
  ADD_ALL_REFERENCES_AFTER_TRAVERSAL: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'add-all-references-after-traversal',
    label: 'Add all references after traversal',
  },
  /** Dependency map only. Infers traversal rules (# of degrees and reference types) from the grouping rules selected by the user. For example, Group by Reference outgoing will automatically traverse that reference outward. This avoids an issue where certain groupings in Dependency Map will not show the nodes expected by the user. */
  GROUPING_IMPLICIT_TRAVERSALS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'grouping-implicit-traversals',
    label: 'Implicit traversals when grouping rules are applied',
  },
  GROUPING_RESEARCH: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'grouping-research',
    label: 'Grouping research',
  },
  GROUPING_BY_TYPE_NAMES: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'grouping-by-type-names',
    label: 'Group by type names',
  },
  ROOT_GROUPS_IN_CONTEXT_ONLY: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'root-groups-in-context-only',
    label:
      'Show only root groups containing the context component in Dependency Map and Timeline',
  },
  /** Allows users to save presentation slides, even for deprecated views. Enabled by special request for certain customers who weren't thrilled with the deprecated status of some views and the limitations imposed. */
  CAN_UPDATE_DEPRECATED_SLIDES: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'can-update-deprecated-slides',
    label: 'Permit updating presentation slides containing deprecated views',
  },
  IP_ALLOWLIST: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ip-whitelisting',
    requiresOrgAdmin: true,
    label:
      'Enable IP blocking in the API. To add/edit allowlist IPs without activating allowlisting in the API, enable "ip-whitelisting-management"',
  },
  IP_ALLOWLIST_MANAGEMENT: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ip-whitelisting-management',
    requiresOrgAdmin: true,
    label: 'Enable the UI in ardoq-front to manage allowlisted IPs',
  },
  HIERARCHICAL_TREEMAP: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'hierarchical-treemap',
    label: 'Hierarchical Treemap',
  },
  SUPPORT_LARGE_DATASETS_EDGE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'support-large-datasets-edge',
    label:
      'This toggles most recent work for support large datasets that is not ready to be viewed by customers with early access.',
  },
  REFERENCE_FAB_PROTOTYPE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-fab-prototype',
    label:
      'Enables the red "Reference fab" button for the new prototype Create Reference flow',
    requiresArdoqEmployee: true,
  },
  PRESENTATIONS_IN_DISCOVER: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'presentations-in-discover',
    label: 'Enables presentations assets in Discover',
  },
  SEMANTIC_SEARCH: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'semantic-search',
    label: 'Enables semantic search in Discover',
  },
  CREATE_COMPONENTS_VIA_SURVEYS_IN_DISCOVER: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'create-components-via-surveys-in-discover',
    label: 'Create components via surveys in discover',
  },
  AUTOCOMPLETE_OPENAI: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'autocomplete-openai',
    label: 'Survey description assistant',
  },
  DISCOVER_PERSONALIZATION: {
    label: 'Discover personalization',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'discover-personalization',
  },
  EDITABLE_REFERENCES_IN_QUESTIONS: {
    label: 'Contributors can edit the references components in surveys',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'editable-references-in-questions',
  },
  SURVEY_IS_COMPLETE_BROADCAST_FILTER: {
    label: 'Survey is complete broadcast filter',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'survey-is-complete-broadcast-filter',
  },
  ALTERNATE_DEPENDENCY_MAP_SORTING: {
    label: 'Alternate dependency map sorting',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'alternate-dependency-map-sorting',
  },
  DISABLE_CONNECTED_PEOPLE: {
    label: 'Disable connected people',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-connected-people',
  },
  BOOKMARKS: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'bookmarks',
    label: `Enable bookmarks, which are basically slides that can, but don't have to be a part of presentation.`,
  },
  DISABLE_DATE_TIME_RANGE: {
    label: 'Disable DateTimeRange fields creation',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-date-time-range',
  },
  COMPONENT_LANDING_PAGE: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'component-landing-page',
    label: 'Component Landing Page',
  },
  FULL_AUDIT_LOG_HISTORY: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'full-audit-log-history',
    label: 'Full Audit Log History',
  },
  LIST_USE_CASES_FROM_OWN_ORG: {
    label: 'List Use Cases (Ardoq Solutions) From Own Organization',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'list-use-cases-from-own-org',
  },
  ENTITY_GROUP_AND_USE_CASE_MANAGEMENT: {
    label: 'Enable Entity Group and Use Case management (for Ardoq Solutions)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'entity-group-and-use-case-management',
  },
  AZURE_OPENAI_VIEW_INFERENCE: {
    label: 'Azure OpenAI View Inference',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-openai-view-inference',
  },
  AZURE_OPENAI_EXPERIMENTAL_FUNCTIONALITY: {
    label: 'Azure OpenAI Experimental Functionality',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-openai-experimental-functionality',
  },
  AZURE_OPENAI_COMPONENT_LINKING_SUGGESTIONS: {
    label: 'Azure OpenAI Component Linking Suggestions',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-openai-component-linking-suggestions',
  },
  AZURE_AUTOMATIC_RESOURCE_GROUP_SELECTION: {
    label: 'Azure Automatic Resource Group Selection',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'azure-automatic-resource-group-selection',
  },
  ADAPTIVE_RELATIONSHIPSVIEW: {
    label: 'Adaptive relationships view layout',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'adaptive-relationshipsview',
  },
  DARK_MODE_TOGGLE: {
    label: 'Dark mode',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'dark-mode-toggle',
  },
  PHOENIX_TO_AMPLIFY: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'phoenix-to-amplify',
    label:
      'This toggles most recent work that we want to present to the Amplify team before it goes to production.',
  },
  PHOENIX_CLOSED_BETA: {
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'phoenix-closed-beta',
    label: 'For letting customers in on pre-release features. Use with care!',
  },
  PROCESS: {
    label: 'ShiftX',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'process',
  },
  SSO_MAPPING_MULTIPLE_PROVIDERS: {
    label: 'SSO mapping multiple providers',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'sso-mapping-multiple-providers',
  },
  LOGIN_PAGE_REFRESH: {
    label: 'Refreshed login page',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'login-page-refresh',
  },
  LOGIN_PAGE_REFRESH_NO_MARKETING_CONTENT: {
    label: 'Refreshed login page with no marketing content',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'login-page-refresh-no-marketing-content',
  },
  TEST_NAVIGATOR_COMPONENT: {
    label: 'Test Navigator Component',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'test-navigator-component',
  },
  QUICK_START: {
    label: 'Quick Start initiative',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'quick-start',
  },
  RESTRICT_USE_CASE_DOWNLOAD: {
    label: 'Quick Start: Restrict use case download',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'restrict-use-case-download',
  },
  QUICK_START_RESTRICT_AVAILABLE_VISUALIZATIONS: {
    label: 'Quick Start: Restrict available visualizations',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'quick-start-restrict-available-visualizations',
  },
  QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS: {
    label: 'Quick Start: Restrict available integrations',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'quick-start-restrict-available-integrations',
  },
  LUCID_CHART_ENABLE: {
    label: 'Enable LucidChart integration',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'lucid-chart-enable',
  },
  LUCID_IMPORT_ENABLE: {
    label: 'Enable LucidChart import',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'lucid-import-enable',
  },
  LUCID_IMPORT_CUSTOM_PROMPT_ENABLE: {
    label: 'Enable LucidChart import custom prompt',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'lucid-import-custom-prompt-enable',
  },
  LUCID_SCENARIO_EXPORT_ENABLE: {
    label: 'Enable LucidChart scenario export',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'lucid-scenario-export-enable',
  },
  DISABLE_CREATE_TEMPLATE_FROM_WORKSPACE_METAMODEL: {
    label: 'Disabled Legacy Feature: Create template from workspace metamodel',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-create-template-workspace-metamodel',
  },
  DISABLE_FIELD_APPLIES_TO_ALL: {
    label:
      'Disabled Legacy Feature: Apply field to all component and reference types',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-field-applies-to-all',
  },
  INVENTORY_ALPHA: {
    label: 'Inventory alpha',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'inventory-alpha',
  },
  INVENTORY_HIERARCHIES: {
    label: 'Beta. Enables hierarchies in Data Inventory.',
    persistedId: 'inventory_tmp_hierarchies',
  },
  CANVAS_PROTOTYPE: {
    label: 'Canvas Prototype',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'canvas-prototype',
  },
  CANVAS_PROTOTYPE_EDGE: {
    label: 'Canvas Prototype (in development)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'canvas-prototype-edge',
  },
  COLLAPSIBLE_SIDE_PANEL: {
    label: 'Collapsible side panel',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'collapsible-side-panel',
  },
  DISABLE_INTEGRATIONS_DASHBOARD: {
    label: 'Disable integrations dashboard',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-integrations-dashboard',
  },
  DISABLE_REFERENCE_CARDINALITY: {
    label: 'Disable reference cardinality',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-reference-cardinality',
  },
  DISABLE_DATE_TIME_FIELDS: {
    label: 'Disable DateTime fields creation',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-date-time-fields',
  },
  NEW_RICH_TEXT_EDITOR: {
    label: 'New Rich Text Editor',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'new-rich-text-editor',
  },
  SURVEYS_CHANGE_APPROVAL_V2: {
    label: 'Surveys Change Approval (v2)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'surveys-change-approval-v2',
  },
  PRIORITY_VIEWPOINTS: {
    label: 'Priority Viewpoints',
    persistedId: 'core-viewpoints_tmp_priority-viewpoints',
  },
  DECLARATIVE_ROUTER: {
    label: 'Declarative Router',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'declarative-router',
  },
  USE_NEW_PRICING_MODULES: {
    label: 'Use new pricing modules',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'use-new-pricing-modules',
  },
  VISIBILITY_MODULE: {
    label: 'Visibility Module',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'visibility-module',
  },
  TRANSFORMATION_MODULE: {
    label: 'Transformation Module',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'transformation-module',
  },
  OVERSIGHT_MODULE: {
    label: 'Oversight Module',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'oversight-module',
  },
  FOUNDATION_SOLUTION_ENABLE: {
    label: 'Enable Foundation Solution',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'foundation-solution-enable',
  },
  DEV_SHOW_ALL_SOLUTIONS: {
    label: 'Dev: Show All Solutions',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'dev-show-all-solutions',
  },
  AI_VALUE_STREAMS: {
    label: 'AI Value Streams',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ai-value-streams',
  },
  AI_CHAT: {
    label: 'AI Chat',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'ai-chat',
  },
  AI_DISCOVER_ASSISTANT: {
    label: 'AI Discover Assistant',
    persistedId: 'ai_tmp_discover-assistant',
  },
  AI_WEB_SEARCH: {
    label: 'AI Web Search',
    persistedId: 'ai_restricted_web-search',
  },
  AI_CAN_ENABLE_WEB_SEARCH: {
    label: 'Can Enable Web Search',
    persistedId: 'ai_restricted_can-enable-web-search',
  },
  AI_TMP_PYTHON_AGENTS: {
    label: 'AI Python Agents',
    persistedId: 'ai_tmp_python-agents',
  },
  AI_TMP_INSIGHT_AGENT: {
    label: 'AI Chat Insight Agent',
    persistedId: 'ai_tmp_insight-agent',
  },
  AI_TMP_CUSTOM_AGENT_EDITING: {
    label: 'Custom Agent Editing',
    persistedId: 'ai_tmp_custom-agent-editing',
    requiresOrgAdmin: true,
  },
  AI_TMP_SUPPORT_OFFICE_DOC_FORMATS: {
    label: 'AI Assistant Office document upload',
    persistedId: 'ai_tmp_support-office-doc-formats',
  },
  AI_FEATURES_ENABLED: {
    label: 'AI features enabled',
    persistedId: 'ai_restricted_features-enable',
  },
  DISABLED_INTERCOM: {
    label: 'Disabled Intercom',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disabled-intercom',
  },
  DISABLE_BEAMER: {
    label: 'Disable Beamer',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-beamer',
  },
  AUTOLOGIN: {
    label: 'Autologin',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'autologin',
  },
  GARTNER_DEMO_FEATURES: {
    label: 'Features for organizations related to Gartner Demo',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'gartner-demo-features',
  },
  REFERENCE_DATA_TOGAF: {
    label: 'TOGAF',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-togaf',
  },
  REFERENCE_DATA_TOGAF_GOVERNMENT_REFERENCE_MODEL: {
    label: 'TOGAF - Government Reference Model (GRM)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-togaf-government-reference-model',
  },
  REFERENCE_DATA_TOGAF_TECHNICAL_REFERENCE_MODEL: {
    label: 'TOGAF - Technical Reference Model (TRM)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-togaf-technical-reference-model',
  },
  REFERENCE_DATA_SUPPLY_CHAIN_MANAGEMENT_ASSOCIATION_SCOR: {
    label: 'Supply Chain Management Association (SCOR)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-supply-chain-management-association-scor',
  },
  REFERENCE_DATA_BUSINESS_ARCHITECTURE_GUILD_BAG: {
    label: 'Business Architecture Guild Common Reference Model (BAG)',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-business-architecture-guild-bag',
  },
  REFERENCE_DATA_BIAN_FRAMEWORK_V12: {
    label: 'BIAN Framework v12.0',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'reference-data-bian-framework-v12',
  },
  OPEN_DIGITAL_ARCHITECTURE: {
    label: 'Open Digital Architecture (ODA)',
    persistedId:
      'integrations_restricted_reference-data-open-digital-architecture',
  },
  /* Discover Core Consolidation */
  DISCOVER_CORE_LANDING_PAGE_AND_NAVIGATION: {
    label: 'New Discover Core landing page and navigation',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'discover-core-landing-page-and-navigation',
  },
  DISCOVER_SCOPED_NAVIGATION: {
    label: 'Discover scoped navigation',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'discover-scoped-navigation',
  },
  /** @deprecated Use hasNewSidebarAndNavbar$ instead */
  DEPRECATED_NEW_SIDEBAR_AND_NAVBAR: {
    label: 'Enable new sidebar and navbar',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'new-sidebar-and-navbar',
  },
  HIDE_LEGACY_DISCOVER_AND_VIEWPOINTS: {
    label:
      'Hide "Open old Discover" banner, "Try new Discover" banner, and legacy Discover viewpoints',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'hide-legacy-discover-and-viewpoints',
  },
  REDIRECT_LEGACY_DISCOVER: {
    label: 'Redirect legacy /discover/* URLs to new-app equivalents',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'redirect-legacy-discover',
  },
  WORKSPACE_WIZARD_2025: {
    label: 'Workspace Wizard 2025',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'workspace-wizard-2025',
  },
  REPORT_FILTERING_IN_PRESENTATIONS: {
    label: 'Report filtering in presentations',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'tmp-report-filtering-in-presentations',
  },
  DEV_MODE_SHOW_FEATURE_FLAGS_IN_APP: {
    label: 'Dev Mode: Show Feature Flags in App',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'dev-mode-show-feature-flags-in-app',
  },
  FIELD_PERMISSIONS: {
    label: 'Field permissions',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'field-permissions',
  },
  FOUNDATION_HOME: {
    label: 'Foundation Home',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'team-navigation-home',
  },
  HOME_PAGE_SETTINGS: {
    label: 'Home Page Settings',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'team-navigation-home-settings',
  },
  PRODUCT_TOUR_AGENT: {
    label: 'Product tour authoring overlay',
    persistedId: 'growth_internal_product-tour-agent',
  },
  SCENARIO_ANALYTICS: {
    label: 'Scenario analytics',
    persistedId: 'scenarios_tmp_analytics',
  },
  BRANCHED_GREMLIN_GRAPH: {
    label: 'Branched Gremlin Graph',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'branched-gremlin-graph',
  },
  DASHBOARD_TMP_NEW_FILTER_TYPES: {
    label: 'New dev dashboard filter types',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'dashboard-tmp-new-filter-types',
  },
  DASHBOARD_TMP_HIERARCHICAL_SUGGESTIONS_FOR_REFERENCE_FILTER: {
    label: 'Reference dashboard filter with hierarchical suggestions',
    persistedId: 'dashboards_tmp_hierarchical-suggestions-for-reference-filter',
  },
  ACCESS_CUSTOMER_SUPPORT_CHAT_PRIVILEGE: {
    label: 'Access Customer Support Chat Privilege',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'access-customer-support-chat-privilege',
  },
  NATURAL_LANGUAGE_TO_QUERY: {
    label: 'Natural language to query',
    persistedId: 'ai_tmp_natural-language-to-query',
  },
  AI_TMP_GREMLIN_QUERYBUILDER: {
    label: 'AI gremlin query builder',
    persistedId: 'ai_tmp_gremlin-querybuilder',
  },
  COMPONENT_OVERVIEW_2025: {
    label: 'Component Overview 2025',
    persistedId: 'component-overview_tmp_component-overview-2025',
  },
  ROADMAP_2026: {
    label: 'WIP reimplementation of the Roadmap view',
    persistedId: 'visualizations_tmp_roadmap-2026',
  },
  ASSISTANT_DIAGRAM: {
    label: 'Assistant Diagram (beta)',
    persistedId: 'visualizations_tmp_assistant-diagram',
  },
  ASSISTANT_BLOCKS: {
    label: 'Assistant Blocks (beta)',
    persistedId: 'visualizations_tmp_assistant-blocks',
  },
  FLEXIBLE_BLOCK_DIAGRAM: {
    label: 'Flexible Block Diagram (beta)',
    persistedId: 'visualizations_tmp_flexible-block-diagram',
  },
  FLEXIBLE_BLOCK_DIAGRAM_AS_BLOCK_DIAGRAM: {
    label: 'Flexible Block Diagram replaces Block Diagram in viewpoints (beta)',
    persistedId: 'visualizations_tmp_flexible-block-diagram-as-block-diagram',
  },
  DISABLE_NEW_DISCOVER_ADMIN_FEATURE_TOGGLE: {
    label: 'Disable new Discover admin feature toggle',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'disable-new-discover-admin-feature-toggle',
  },
  TEAM_CORE_EXPERIMENTAL_FEATURES: {
    label: 'Team core experimental features',
    persistedId: 'core_internal_experimental-features',
  },
  TEAM_CORE_RELEASE_JUNE_19: {
    label: 'Team core release June 19',
    persistedId: 'core_tmp_team-core-release-june-19',
  },
  WORKSPACE_METAMODEL_CONSTRAINTS: {
    label: 'Workspace metamodel constraints',
    persistedId: 'core_tmp_workspace-metamodel-constraints',
  },
  GLOBAL_TYPES: {
    label: 'Global types',
    persistedId: 'core_tmp_global-types',
  },
  SOLUTIONS_METAMODEL_LINK: {
    label: 'Metamodel link on solutions page',
    persistedId: 'solutions_tmp_metamodel-link',
  },
  PREFER_OPENING_WORKSPACES_IN_INVENTORY: {
    label: 'Open workspace data in inventory by default instead of workspace',
    persistedId: 'inventory_tmp_prefer-opening-workspaces-in-inventory',
  },
  PREFER_OPENING_WORKSPACES_IN_WORKSPACE: {
    label: 'Open workspace data in workspace by default instead of inventory',
    persistedId: 'core_tmp_prefer-opening-workspaces-in-workspace',
  },
  FIELD_IMPACT_ANALYSIS_BETA: {
    label: 'Field impact analysis beta',
    persistedId: 'core_tmp_field-impact-analysis-beta',
  },
  GRAPHLAKE_ALPHA: {
    label: 'Graphlake alpha',
    persistedId: 'core_tmp_graphlake-alpha',
  },
  ENABLE_ONBOARDING_STEPS: {
    label: 'Enable Onboarding Steps Widget',
    // @ts-expect-error Intentionally omits domain_scope prefix
    persistedId: 'enable-onboarding-steps',
  },
  AUDIT_LOG_FILTER_BUTTON: {
    label: 'Audit log filter button',
    persistedId: 'audit-log_tmp_filter-button',
  },
  REFERENCE_BUBBLE_CHART_IN_VIEWPOINT_MODE: {
    label: 'Reference Bubble Chart in Viewpoint Mode',
    persistedId: 'visualizations_tmp_reference-bubble-chart-in-viewpoint-mode',
  },
  ACCESS_OVERVIEW_NEW_FILTER_BAR: {
    label: 'Access overview new filter bar',
    persistedId: 'access_tmp_access-overview-new-filter-bar',
  },
  LANGUAGE_SELECTION: {
    label: 'Enable Beta UI language options',
    persistedId: 'core_restricted_ui-language-selection',
  },
  TRANSFORMATIONS: {
    label: 'WIP transformations',
    persistedId: 'transformations_tmp_transformations',
  },
  TRANSFORMATIONS_ALPHA: {
    label: 'WIP transformations alpha features',
    persistedId: 'transformations_tmp_alpha',
  },
  CONTENT_AWARE_BUNDLE_COPY: {
    label: 'Content-aware bundle copy',
    persistedId: 'core_tmp_content-aware-bundle-copy',
  },
  FIELD_GROUPS: {
    label: 'Field groups',
    persistedId: 'core_tmp_field-groups',
  },
  PERSON_IN_GRAPH_PROTOTYPE: {
    label: 'Person in Graph prototype',
    persistedId: 'access_tmp_person-in-graph-prototype',
  },
  PERSON_IN_GRAPH_PERMISSIONS_PROTOTYPE: {
    label: 'Person in Graph permissions prototype',
    persistedId: 'access_tmp_person-in-graph-permissions-prototype',
  },
  VISUALIZATIONS_TMP_AI_VIEW_CONFIGURATION: {
    label: 'AI view configuration (incl. conditional formatting)',
    persistedId: 'visualizations_tmp_ai-view-configuration',
  },
  VISUALIZATIONS_TMP_AI_VIEW_CONFIGURATION_EXTENDED: {
    label: 'AI view configuration (for labels, viewsettings, grouping)',
    persistedId: 'visualizations_tmp_ai-view-configuration-extended',
  },
  VISUALIZATIONS_BLOCK_DIAGRAM_SVG_EXPORT: {
    label: 'Vector SVG export for block diagram (viewpoint mode)',
    persistedId: 'visualizations_internal_block-diagram-svg-export',
  },
  RENAME_FIELD: {
    label: 'Field renaming',
    persistedId: 'core_tmp_rename-field',
  },
  ASSETS_OVERVIEW_AG_GRID: {
    label: 'Render asset overview pages with the new table component',
    persistedId: 'design-system_tmp_assets-overview-ag-grid',
  },
  IN_VIEW_REFERENCE_CREATION: {
    label: 'In-view reference creation',
    persistedId: 'core_tmp_in-view-reference-creation',
  },
  USAGE_METRICS_TMP_EXPORT_CSV: {
    label: 'Usage metrics export CSV download button',
    persistedId: 'dashboards_tmp_usage-metrics-export-csv',
  },
  DASHBOARDS_TMP_POWERBI_EMBED: {
    label: 'PowerBI embed widget in dashboards',
    persistedId: 'dashboards_tmp_powerbi-embed',
  },
  DISABLE_WORKSPACE_MODE: {
    label: 'Disable workspace mode',
    persistedId: 'core_tmp_disable-workspace-mode',
  },
  ADMIN_IMPERSONATION: {
    label: 'Admin impersonation',
    persistedId: 'appsec_restricted_admin-impersonation',
    requiresOrgAdmin: true,
  },
  MCP_OAUTH: {
    label: 'Ardoq MCP via OAuth',
    // @ts-expect-error Predates FeatureFlagType
    persistedId: 'mcp-oauth',
  },
  IMPORT_BUILDER_AI: {
    label: 'Import Builder AI Configure',
    persistedId: 'integrations_restricted_import-builder-ai-configure',
  },
  USE_INTEGRATIONS_NEW_ROUTER: {
    label: 'Use integrations new router',
    persistedId: 'integrations_tmp_use-new-router',
  },
  SANDBOX_INDICATOR: {
    label: 'Sandbox indicator',
    persistedId: 'core_tmp_sandbox-indicator',
  },
  EDIT_CUSTOM_AGENTS: {
    label: 'Edit custom agents',
    persistedId: 'ai_restricted_edit-custom-agents',
  },
  IMPROVED_DATASET_DEFINITION: {
    label: 'Improved dataset definition',
    persistedId: 'core_tmp_improved-dataset-definition',
  },
  GROWTH_TMP_DASHBOARD_SAVE_BUBBLE_CHART_WIDGET: {
    label: 'Support saving bubble chart as a widget',
    persistedId: 'growth_tmp_dashboard-save-bubble-chart-widget',
  },
  VISIBILITY_TMP_APPLICATION_COMPLETENESS_PAGE: {
    label: 'Application completeness page',
    persistedId: 'visibility_tmp_application-completeness-page',
  },
} satisfies Record<string, StrictFeature>;

export type FeatureKeys = keyof typeof features;

export const Features: Record<FeatureKeys, Feature> = features;

export const FeatureName = Object.fromEntries(
  (Object.keys(Features) as FeatureKeys[]).map(
    featureKey => [featureKey, featureKey] as const
  )
) as Record<FeatureKeys, FeatureKeys>;
